import { Component, EventEmitter, Input, Output, Pipe, PipeTransform, forwardRef } from '@angular/core';
import { FormGroup, FormControl } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import {
  CopyToClipboardModule,
  SimpleButtonComponent,
  SimpleInputComponent,
  TitleContentComponent,
} from 'ngx-zcs-components';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AuthenticationService, GenericDeviceValidator, UserPermissions } from 'ngx-core-services';
import { defer } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

import { CreateFirebaseBlockService } from '../create-firebase/create-firebase-block.service';
import { ConnextUsersForm } from './users-section-models';
import { UserEmailValidatorPipe } from '../pipes/errors.pipe';
import { labels } from '../create-block/create.model';

export type ConnextUserForm = FormGroup<{
  email: FormControl<string | null>;
  userLevel: FormControl<UserPermissions | null>;
  added: FormControl<boolean | null>;
  isSelected: FormControl<boolean | null>;
}>;

@Pipe({
  name: 'userPermission',
  standalone: true,
})
export class UserPermissionPipe implements PipeTransform {
  transform(userLevel: UserPermissions): string {
    switch (userLevel) {
      case UserPermissions.Owner:
        return labels.owner;
      case UserPermissions.Admin:
        return labels.admin;
      case UserPermissions.Installer:
        return labels.installer;
      case UserPermissions.viewer:
        return labels.viewer;
      default:
        return '';
    }
  }
}

@Component({
  standalone: true,
  imports: [
    CommonModule,
    CopyToClipboardModule,
    forwardRef(() => SimpleButtonComponent),
    forwardRef(() => SimpleInputComponent),
    forwardRef(() => TitleContentComponent),
    MatCheckboxModule,
    MatFormFieldModule,
    MatIconModule,
    MatSelectModule,
    MatTooltipModule,
    TranslateModule,
    UserEmailValidatorPipe,
    UserPermissionPipe,
  ],
  selector: 'customer-users-section',
  templateUrl: './users-section.component.html',
  styleUrls: ['./users-section.component.scss'],
})
export class UsersSectionComponent {
  @Input() connextUsers!: ConnextUsersForm;
  @Input() connextSerial?: string | null;
  @Input() isVerifiedMode = false;
  @Input() showUnsavedChanges = true;

  /** When true, "Delete Selected" emits (deleteUsers) instead of removing locally.
   *  The parent handles the confirmation dialog and mutation. */
  @Input() useDirectDelete = false;


  readonly _labels = labels;
  @Input() set labels(value: Partial<typeof this._labels>) {
    Object.assign(this._labels, value);
  }
  get labels(): typeof this._labels {
    return this._labels;
  }

  @Output() onUserChanges = new EventEmitter<ConnextUsersForm>();

  /** Emits the emails of selected users to be deleted by the parent (smart component). */
  @Output() deleteUsers = new EventEmitter<string[]>();

  UserPermissions = UserPermissions;

  /** Email of the last unselected Owner — its checkbox is disabled to prevent selecting
   *  all Owners for deletion. Recalculates only when FormArray values change. */
  readonly disabledOwnerEmail$ = defer(() => this.connextUsers.controls.users.valueChanges.pipe(
    startWith(this.connextUsers.controls.users.value),
    map((users) => {
      const unselected = users.filter((u) => u.added && u.userLevel === UserPermissions.Owner && !u.isSelected);
      return unselected.length === 1 ? (unselected[0].email ?? null) : null;
    }),
  ));

  get myEmail() {
    return this.authenticationService.user$.value?.email?.toLowerCase();
  }

  get latestUser() {
    return this.connextUsers.controls.users.at(this.connextUsers.controls.users.length - 1);
  }

  get isAnyUserSelected() {
    return this.connextUsers.controls.users.value.some((user: any) => user.isSelected);
  }

  get userPermissions() {
    return this.isVerifiedMode
      ? [UserPermissions.Owner, UserPermissions.Admin, UserPermissions.Installer, UserPermissions.viewer]
      : [UserPermissions.Owner, UserPermissions.Admin];
  }

  constructor(
    private readonly authenticationService: AuthenticationService,
    private readonly genericDeviceValidator: GenericDeviceValidator,
    readonly createFirebaseBlockService: CreateFirebaseBlockService,
  ) {
    this.updateUserInConnext = this.updateUserInConnext.bind(this);
    this.emitDeleteUsers = this.emitDeleteUsers.bind(this);
    this.removeSelectedUsers = this.removeSelectedUsers.bind(this);
    this.removeUsers = this.removeUsers.bind(this);
  }

  updatePermissions(user: ConnextUserForm, userLevel: UserPermissions) {
    user.patchValue({ userLevel });
    this.connextUsers.controls.isModified.patchValue(true);

    if (!this.isVerifiedMode) {
      this.updateUserInConnext(user)();
    }
  }

  onUserChangeWrapper() {
    return () => this.onUserChanges.emit(this.connextUsers);
  }

  /** Collects selected user emails and emits them to the parent for deletion. */
  emitDeleteUsers() {
    const emails = this.connextUsers.controls.users.value
      .filter((user) => user.isSelected && user.added)
      .map((user) => user.email!);
    if (emails.length) {
      this.deleteUsers.emit(emails);
    }
  }

  removeUsers() {
    const selectedUsers = this.connextUsers.controls.users.value.filter((user) => user.isSelected);
    if (!selectedUsers.length) {
      return;
    }

    selectedUsers.forEach((user) => {
      const index = this.connextUsers.controls.users.value.findIndex((u) => u.email === user.email);
      this.connextUsers.controls.users.removeAt(index);
    });
  }

  updateUserInConnext(user: ConnextUserForm) {
    return async () => {
      if (!this.connextSerial) {
        return;
      }

      const uid = await this.genericDeviceValidator.getConnextUid(this.connextSerial);
      if (!uid) {
        return;
      }

      const { email, userLevel } = user.value;
      if (!email || typeof userLevel !== 'number') {
        return;
      }

      await this.createFirebaseBlockService.updateUserInConnext(email, userLevel, uid, this.labels.updateUsersAuthorizations);
    };
  }

  async removeSelectedUsers() {
    const selectedUsers = this.connextUsers.controls.users.value.filter((user: any) => user.isSelected);
    if (!selectedUsers.length) {
      return;
    }

    if (!this.connextSerial) {
      return;
    }

    const uid = await this.genericDeviceValidator.getConnextUid(this.connextSerial);
    if (!uid) {
      return;
    }

    // eslint-disable-next-line @typescript-eslint/prefer-for-of
    for (let index = 0; index < selectedUsers.length; index++) {
      const { email, userLevel } = selectedUsers[index];
      if (!email || typeof userLevel !== 'number') {
        continue;
      }
      await this.createFirebaseBlockService.updateUserInConnext(email, userLevel, uid, this.labels.updateUsersAuthorizations, true);
    }
  }
}
